import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import SiiLogo from '@fingo/lib/assets/icons/logos/sii';
import UploadInvoicesIcon from '@fingo/lib/assets/icons/itau/UploadInvoicesIcon';
import { useGetCurrenciesFromCountry } from '@fingo/lib/hooks/useGetCurrencies';
import { REQUESTING_PLATFORM_TO_CODES } from '@fingo/lib/constants/requesting-platform';
import { getCurrenciesForPlatform } from '@fingo/lib/constants/requesting-platform-currencies';
import UploadFlowDialog from './UploadFlowDialog';
import UploadInvoicesMethodOption from './UploadInvoicesMethodOption';

export const UPLOAD_INVOICES_METHODS = {
  SII: 'sii',
  MANUAL: 'manual',
};

export const UPLOAD_NOMINA_METHOD_LABEL = 'Cargar mediante nómina';

const UploadInvoicesMethodDialog = ({ open, onClose, onContinue }) => {
  const { t } = useTranslation();
  const [selectedMethod, setSelectedMethod] = useState(UPLOAD_INVOICES_METHODS.MANUAL);
  const countryCurrencies = useGetCurrenciesFromCountry().map((c) => c.isoCode);
  const manualMethodCurrencies = getCurrenciesForPlatform(
    REQUESTING_PLATFORM_TO_CODES.ITAU,
    countryCurrencies,
  );

  return (
    <UploadFlowDialog
      open={open}
      onClose={onClose}
      title={(
        <Typography variant="medium_2" fontWeight={700} align="center" sx={{ color: 'gray.A600' }}>
          ¿Cómo quieres cargar tus facturas?
        </Typography>
      )}
      actions={(
        <Stack direction="row" spacing={1} justifyContent="center" width="100%">
          <Button variant="outlined" color="primary" onClick={onClose} size="large_1">
            <Typography variant="small_3" fontWeight={700} sx={{ color: 'primary.main' }}>
              Cancelar
            </Typography>
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large_1"
            onClick={() => onContinue(selectedMethod)}
          >
            <Typography variant="small_3" fontWeight={700} sx={{ color: 'white' }}>
              Continuar
            </Typography>
          </Button>
        </Stack>
      )}
    >
      <FormControl component="fieldset" sx={{ maxWidth: '100%' }}>
        <RadioGroup
          aria-label="upload-invoices-method"
          name="upload-invoices-method"
          value={selectedMethod}
          onChange={(event) => setSelectedMethod(event.target.value)}
        >
          <Stack spacing={3}>
            <UploadInvoicesMethodOption
              value={UPLOAD_INVOICES_METHODS.SII}
              label={`Sincronizar con el ${t('National Tax Service acronym')}`}
              description={`Importa automáticamente tus facturas desde el ${t('National Tax Service acronym')} y mantén tu información actualizada.`}
              recommended
              iconSrc={SiiLogo}
              currencies={[t('default currency')]}
              onSelect={setSelectedMethod}
            />
            <UploadInvoicesMethodOption
              value={UPLOAD_INVOICES_METHODS.MANUAL}
              label={UPLOAD_NOMINA_METHOD_LABEL}
              description="Sube una nómina con las facturas que deseas financiar."
              Icon={UploadInvoicesIcon}
              currencies={manualMethodCurrencies}
              onSelect={setSelectedMethod}
            />
          </Stack>
        </RadioGroup>
      </FormControl>
    </UploadFlowDialog>
  );
};

UploadInvoicesMethodDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
};

export default UploadInvoicesMethodDialog;
